import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Customer {
  id: string
  name: string
  segment: string
  lifetime_value: number
  health_score: number
  last_purchase_days: number
  status: string
}

interface Segment {
  id: string
  name: string
  customer_count: number
  avg_ltv: number
  avg_satisfaction: number
  churn_risk: string
}

interface Statistics {
  total_customers: number
  active_customers: number
  avg_ltv: number
  avg_satisfaction: number
  churn_rate: number
  nps_score: number
  segments: number
  at_risk_customers: number
}

export default function CustomerIntelligencePage() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [segments, setSegments] = useState<Segment[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'segments' | 'customers'>('segments')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [segmentsRes, behaviorRes, statsRes] = await Promise.all([
        fetch('/api/customerintelligence/segments'),
        fetch('/api/customerintelligence/behavior'),
        fetch('/api/customerintelligence/statistics')
      ])

      if (segmentsRes.ok) setSegments(await segmentsRes.json())
      if (behaviorRes.ok) setCustomers(await behaviorRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching customer intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      at_risk: 'bg-yellow-100 text-yellow-800',
      churned: 'bg-red-100 text-red-800',
      new: 'bg-blue-100 text-blue-800',
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    }
    return classes[status.toLowerCase().replace(' ', '_')] || 'bg-gray-100 text-gray-800'
  }

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Customer Intelligence</h1>
          <p className="mt-2 text-gray-600">AI-powered customer analytics and segmentation insights</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_customers.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg LTV</p>
              <p className="text-2xl font-bold text-blue-600">${statistics.avg_ltv.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">NPS Score</p>
              <p className="text-2xl font-bold text-green-600">{statistics.nps_score}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Churn Rate</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.churn_rate.toFixed(1)}%</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('segments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'segments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Segments ({segments.length})
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'customers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Customer Behavior
            </button>
          </nav>
        </div>

        {/* Segments Cards */}
        {activeTab === 'segments' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {segments.map((segment) => (
              <div key={segment.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-semibold text-gray-900">{segment.name}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(segment.churn_risk)}`}>
                    {segment.churn_risk} risk
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Customers</span>
                    <span className="text-sm font-medium text-gray-900">{segment.customer_count.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Avg LTV</span>
                    <span className="text-sm font-medium text-gray-900">${segment.avg_ltv.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Satisfaction</span>
                    <span className="text-sm font-medium text-gray-900">{segment.avg_satisfaction.toFixed(1)}/5</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Customers Table */}
        {activeTab === 'customers' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LTV</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Health</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Purchase</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.slice(0, 20).map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.segment}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${customer.lifetime_value.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${getHealthColor(customer.health_score)}`}
                            style={{ width: `${customer.health_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{customer.health_score}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.last_purchase_days} days ago
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(customer.status)}`}>
                        {customer.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
